// =============================================
// /src/pages/AvatarFrameGroupForm.tsx
// 新規/編集 共通フォームコンポーネント
// =============================================
import React from 'react';
import firebase from 'firebase/app';
import type { AvatarFrameGroup, FrameEntry, Rank } from '../types/avatarFrame';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch as MUISwitch,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/UploadFile';
import ClearIcon from '@mui/icons-material/Clear';

/* ========= Timestamp <-> datetime-local ========= */
const pad = (n: number) => String(n).padStart(2, '0');
const ts2input = (ts?: firebase.firestore.Timestamp) => {
  if (!ts) return '';
  const d = ts.toDate();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};
const input2ts = (v?: string): firebase.firestore.Timestamp | undefined => {
  if (!v) return undefined;
  const d = new Date(v);
  if (isNaN(d.getTime())) return undefined;
  return firebase.firestore.Timestamp.fromDate(d);
};

/* ========= ランク候補 ========= */
const RANKS: Rank[] = ['bronze', 'silver', 'gold', 'platinum', 'admin'];

/* ========= フォーム用値 ========= */
export type AvatarFrameGroupFormValue = Omit<AvatarFrameGroup, 'lastRefreshAt'>;

export const emptyGroup = (): AvatarFrameGroupFormValue => ({
  id: '',
  name: '',
  description: '',
  enabled: true,
  order: 0,
  publicOpen: false, // 任意だがUI既定は false
  frames: [],
});

/* ========= フレーム1行（画像＋各項目） ========= */
type FrameRowProps = {
  frame: FrameEntry;
  onChange: (next: FrameEntry) => void;
  onRemove: () => void;
  onUpload: (file: File) => void;
  onClearImage: () => void;
};

const FrameRow: React.FC<FrameRowProps> = ({
  frame,
  onChange,
  onRemove,
  onUpload,
  onClearImage,
}) => {
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#f5f5f5',
          }}
        >
          {frame.uri ? (
            <img
              src={frame.uri}
              alt={frame.label ?? frame.id}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}
        </Box>

        <TextField
          label="Frame ID"
          size="small"
          value={frame.id}
          onChange={(e) => onChange({ ...frame, id: e.target.value })}
          sx={{ width: 160 }}
        />

        <TextField
          label="表示ラベル"
          size="small"
          value={frame.label ?? ''}
          onChange={(e) => onChange({ ...frame, label: e.target.value })}
          sx={{ width: 180 }}
        />

        <TextField
          label="順序"
          type="number"
          size="small"
          value={frame.order}
          onChange={(e) =>
            onChange({ ...frame, order: Number(e.target.value) })
          }
          sx={{ width: 110 }}
        />

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">有効</Typography>
          <MUISwitch
            checked={!!frame.enabled}
            onChange={(_, c) => onChange({ ...frame, enabled: c })}
          />
        </Stack>

        <TextField
          label="pxWidth"
          type="number"
          size="small"
          value={frame.pxWidth ?? ''}
          onChange={(e) =>
            onChange({
              ...frame,
              pxWidth:
                e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          sx={{ width: 120 }}
        />

        <TextField
          label="pxHeight"
          type="number"
          size="small"
          value={frame.pxHeight ?? ''}
          onChange={(e) =>
            onChange({
              ...frame,
              pxHeight:
                e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          sx={{ width: 120 }}
        />

        <TextField
          label="画像URL (uri)"
          size="small"
          value={frame.uri ?? ''}
          onChange={(e) =>
            onChange({ ...frame, uri: e.target.value || undefined })
          }
          sx={{ minWidth: 280, flex: 1 }}
        />

        <Chip
          label={frame.v ? `v:${frame.v}` : '未アップロード'}
          size="small"
        />

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
            if (fileRef.current) fileRef.current.value = '';
          }}
        />
        <Button
          startIcon={<UploadIcon />}
          variant="outlined"
          size="small"
          onClick={() => fileRef.current?.click()}
        >
          画像アップロード/差し替え
        </Button>

        <IconButton
          size="small"
          onClick={onClearImage}
          title="画像URLとバージョンをクリア"
        >
          <ClearIcon />
        </IconButton>

        <IconButton
          color="error"
          size="small"
          onClick={onRemove}
          title="このフレームを削除"
        >
          <DeleteIcon />
        </IconButton>
      </Stack>
    </Paper>
  );
};

/* ========= メインフォーム ========= */
export const AvatarFrameGroupForm: React.FC<{
  mode: 'new' | 'edit';
  value: AvatarFrameGroupFormValue;
  onChange: (next: AvatarFrameGroupFormValue) => void;
  onUpload: (frameIndex: number, file: File) => void; // ← ここで親のアップロードAPIを呼ぶ
  onSubmit: () => void;
  submitting?: boolean;
}> = ({ mode, value, onChange, onUpload, onSubmit, submitting }) => {
  // datetime-local 双方向同期
  const [startAtLocal, setStartAtLocal] = React.useState<string>(
    ts2input(value.startAt)
  );
  const [endAtLocal, setEndAtLocal] = React.useState<string>(
    ts2input(value.endAt)
  );
  React.useEffect(
    () => setStartAtLocal(ts2input(value.startAt)),
    [value.startAt]
  );
  React.useEffect(() => setEndAtLocal(ts2input(value.endAt)), [value.endAt]);

  // requiredGrants（カンマ区切り）
  const grantsText = (value.requiredGrants ?? []).join(', ');
  const handleGrantsText = (txt: string) => {
    const arr = txt
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    onChange({ ...value, requiredGrants: arr.length ? arr : undefined });
  };

  // allowedRanks（複数選択）
  const handleAllowedRanks = (ranks: Rank[]) => {
    onChange({ ...value, allowedRanks: ranks.length ? ranks : undefined });
  };

  // frames 操作
  const addFrame = () => {
    const next: FrameEntry = {
      id: '',
      order: value.frames.length,
      enabled: true,
    };
    onChange({ ...value, frames: [...value.frames, next] });
  };
  const updateFrameAt = (idx: number, next: FrameEntry) => {
    const frames = value.frames.slice();
    frames[idx] = next;
    onChange({ ...value, frames });
  };
  const removeFrameAt = (idx: number) => {
    const frames = value.frames.slice();
    frames.splice(idx, 1);
    onChange({ ...value, frames });
  };
  const clearFrameImageAt = (idx: number) => {
    const f = value.frames[idx];
    const frames = value.frames.slice();
    frames[idx] = { ...f, uri: undefined, v: undefined };
    onChange({ ...value, frames });
  };

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        {/* 基本情報 */}
        <Grid item xs={12} md={6}>
          <TextField
            label="グループID"
            fullWidth
            size="small"
            value={value.id}
            disabled={mode === 'edit'}
            onChange={(e) => onChange({ ...value, id: e.target.value })}
            helperText={
              mode === 'edit'
                ? 'グループIDは変更できません'
                : '半角英数字・ハイフンなど'
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="表示名"
            fullWidth
            size="small"
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="説明"
            fullWidth
            size="small"
            value={value.description ?? ''}
            onChange={(e) =>
              onChange({ ...value, description: e.target.value || undefined })
            }
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            label="並び順"
            type="number"
            fullWidth
            size="small"
            value={value.order}
            onChange={(e) =>
              onChange({ ...value, order: Number(e.target.value) })
            }
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body2">配布ON/OFF</Typography>
            <MUISwitch
              checked={!!value.enabled}
              onChange={(_, c) => onChange({ ...value, enabled: c })}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body2">
              全員公開（requiredGrants無視）
            </Typography>
            <MUISwitch
              checked={!!value.publicOpen}
              onChange={(_, c) => onChange({ ...value, publicOpen: c })}
            />
          </Stack>
        </Grid>

        {/* ランク条件 */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="min-rank-label">最小ランク(minRank)</InputLabel>
            <Select
              labelId="min-rank-label"
              label="最小ランク(minRank)"
              value={value.minRank ?? ''}
              onChange={(e) =>
                onChange({
                  ...value,
                  minRank: (e.target.value || undefined) as Rank | undefined,
                })
              }
              displayEmpty
            >
              <MenuItem value="">
                <em>(指定なし)</em>
              </MenuItem>
              {RANKS.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="allowed-ranks-label">
              許可ランク(allowedRanks)
            </InputLabel>
            <Select
              multiple
              labelId="allowed-ranks-label"
              label="許可ランク(allowedRanks)"
              value={value.allowedRanks ?? []}
              onChange={(e) => handleAllowedRanks(e.target.value as Rank[])}
              renderValue={(selected) => (selected as string[]).join(', ')}
            >
              {RANKS.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* requiredGrants（カンマ区切り） */}
        <Grid item xs={12}>
          <TextField
            label='requiredGrants（例: "coupon.vip2025, pack.neko, event.2025-11"）'
            fullWidth
            size="small"
            value={grantsText}
            onChange={(e) => handleGrantsText(e.target.value)}
          />
        </Grid>

        {/* 開始・終了 */}
        <Grid item xs={12} md={6}>
          <TextField
            label="開始(startAt)"
            type="datetime-local"
            fullWidth
            size="small"
            value={startAtLocal}
            onChange={(e) => {
              setStartAtLocal(e.target.value);
              onChange({ ...value, startAt: input2ts(e.target.value) });
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="終了(endAt)"
            type="datetime-local"
            fullWidth
            size="small"
            value={endAtLocal}
            onChange={(e) => {
              setEndAtLocal(e.target.value);
              onChange({ ...value, endAt: input2ts(e.target.value) });
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Divider />

      {/* ====== フレーム一覧（画像UIあり） ====== */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">フレーム一覧</Typography>
        <Button variant="outlined" onClick={addFrame}>
          フレームを追加
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        {value.frames.map((f, i) => (
          <FrameRow
            // key={`${f.id || 'new'}-${i}`}
            key={i} // ← 入力中に変わらないキーにする
            frame={f}
            onChange={(next) => updateFrameAt(i, next)}
            onRemove={() => removeFrameAt(i)}
            onUpload={(file) => onUpload(i, file)}
            onClearImage={() => clearFrameImageAt(i)}
          />
        ))}
        {value.frames.length === 0 && (
          <Typography color="text.secondary">
            フレームがありません。「フレームを追加」を押してください。
          </Typography>
        )}
      </Stack>

      <Divider />

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={onSubmit} disabled={submitting}>
          {mode === 'new' ? '作成' : '更新'}
        </Button>
      </Stack>
    </Stack>
  );
};

// import React from 'react';
// import type { AvatarFrameGroup, FrameEntry } from '../types/avatarFrame';
// import {
//   Box,
//   Button,
//   Chip,
//   Divider,
//   Grid,
//   IconButton,
//   Stack,
//   Switch as MUISwitch,
//   TextField,
//   Typography,
// } from '@mui/material';
// import DeleteIcon from '@mui/icons-material/Delete';
// import UploadIcon from '@mui/icons-material/UploadFile';

// export type FormMode = 'new' | 'edit';

// export type AvatarFrameGroupFormValue = Omit<AvatarFrameGroup, 'lastRefreshAt'>;

// export const emptyGroup = (): AvatarFrameGroupFormValue => ({
//   id: '',
//   name: '',
//   description: '',
//   enabled: true,
//   order: 0,
//   frames: [],
// });

// // ---- Frame Editor ----
// const FrameRow: React.FC<{
//   frame: FrameEntry;
//   onChange: (next: FrameEntry) => void;
//   onRemove: () => void;
//   onUpload: (file: File) => void;
// }> = ({ frame, onChange, onRemove, onUpload }) => {
//   const inputRef = React.useRef<HTMLInputElement | null>(null);
//   return (
//     <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
//       <Box
//         sx={{
//           width: 72,
//           height: 72,
//           borderRadius: 2,
//           overflow: 'hidden',
//           bgcolor: '#f5f5f5',
//         }}
//       >
//         {frame.uri ? (
//           <img
//             src={frame.uri}
//             alt={frame.label}
//             style={{ width: '100%', height: '100%', objectFit: 'cover' }}
//           />
//         ) : null}
//       </Box>
//       <TextField
//         label="Frame ID"
//         size="small"
//         value={frame.id}
//         onChange={(e) => onChange({ ...frame, id: e.target.value })}
//       />
//       <TextField
//         label="表示ラベル"
//         size="small"
//         value={frame.label ?? ''}
//         onChange={(e) => onChange({ ...frame, label: e.target.value })}
//       />
//       <TextField
//         label="順序"
//         type="number"
//         size="small"
//         value={frame.order}
//         onChange={(e) => onChange({ ...frame, order: Number(e.target.value) })}
//         sx={{ width: 100 }}
//       />
//       <Stack direction="row" alignItems="center" spacing={1}>
//         <Typography variant="body2">有効</Typography>
//         <MUISwitch
//           checked={frame.enabled}
//           onChange={(_, c) => onChange({ ...frame, enabled: c })}
//         />
//       </Stack>
//       <Chip label={frame.v ? `v:${frame.v}` : '未アップロード'} size="small" />
//       <input
//         ref={inputRef}
//         type="file"
//         accept="image/*"
//         hidden
//         onChange={(e) => {
//           const file = e.target.files?.[0];
//           if (file) onUpload(file);
//           if (inputRef.current) inputRef.current.value = '';
//         }}
//       />
//       <Button
//         startIcon={<UploadIcon />}
//         variant="outlined"
//         size="small"
//         onClick={() => inputRef.current?.click()}
//       >
//         画像アップロード/差し替え
//       </Button>
//       <IconButton color="error" onClick={onRemove}>
//         <DeleteIcon />
//       </IconButton>
//     </Stack>
//   );
// };

// export const AvatarFrameGroupForm: React.FC<{
//   mode: FormMode;
//   value: AvatarFrameGroupFormValue;
//   onChange: (next: AvatarFrameGroupFormValue) => void;
//   onUpload: (frameIndex: number, file: File) => void;
//   onSubmit: () => void;
//   submitting?: boolean;
// }> = ({ mode, value, onChange, onUpload, onSubmit, submitting }) => {
//   const addFrame = () => {
//     const next: FrameEntry = {
//       id: '',
//       order: value.frames.length,
//       enabled: true,
//     } as FrameEntry;
//     onChange({ ...value, frames: [...value.frames, next] });
//   };

//   const updateFrameAt = (idx: number, next: FrameEntry) => {
//     const frames = value.frames.slice();
//     frames[idx] = next;
//     onChange({ ...value, frames });
//   };

//   const removeFrameAt = (idx: number) => {
//     const frames = value.frames.slice();
//     frames.splice(idx, 1);
//     onChange({ ...value, frames });
//   };

//   return (
//     <Stack spacing={3}>
//       <Grid container spacing={2}>
//         <Grid item xs={12} md={6}>
//           <TextField
//             label="グループID"
//             fullWidth
//             size="small"
//             value={value.id}
//             disabled={mode === 'edit'}
//             onChange={(e) => onChange({ ...value, id: e.target.value })}
//             helperText={
//               mode === 'edit'
//                 ? 'グループIDは変更できません'
//                 : '半角英数字・ハイフンなど'
//             }
//           />
//         </Grid>
//         <Grid item xs={12} md={6}>
//           <TextField
//             label="表示名"
//             fullWidth
//             size="small"
//             value={value.name}
//             onChange={(e) => onChange({ ...value, name: e.target.value })}
//           />
//         </Grid>
//         <Grid item xs={12}>
//           <TextField
//             label="説明"
//             fullWidth
//             size="small"
//             value={value.description ?? ''}
//             onChange={(e) =>
//               onChange({ ...value, description: e.target.value })
//             }
//           />
//         </Grid>
//         {/* 公開設定 */}
//         <Grid item xs={12} md={3}>
//           <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
//             <Typography variant="body2">
//               全員公開（requiredGrants無視）
//             </Typography>
//             <MUISwitch
//               checked={value.publicOpen ?? false}
//               onChange={(_, c) => onChange({ ...value, publicOpen: c })}
//             />
//           </Stack>
//         </Grid>
//         <Grid item xs={12} md={3}>
//           <TextField
//             label="並び順"
//             type="number"
//             fullWidth
//             size="small"
//             value={value.order}
//             onChange={(e) =>
//               onChange({ ...value, order: Number(e.target.value) })
//             }
//           />
//         </Grid>
//         <Grid item xs={12} md={3}>
//           <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
//             <Typography variant="body2">配布ON/OFF</Typography>
//             <MUISwitch
//               checked={value.enabled}
//               onChange={(_, c) => onChange({ ...value, enabled: c })}
//             />
//           </Stack>
//         </Grid>
//       </Grid>

//       <Divider />
//       <Stack direction="row" justifyContent="space-between" alignItems="center">
//         <Typography variant="h6">フレーム一覧</Typography>
//         <Button variant="outlined" onClick={addFrame}>
//           フレームを追加
//         </Button>
//       </Stack>

//       {value.frames.map((f, i) => (
//         <FrameRow
//           key={i}
//           frame={f}
//           onChange={(next) => updateFrameAt(i, next)}
//           onRemove={() => removeFrameAt(i)}
//           onUpload={(file) => onUpload(i, file)}
//         />
//       ))}

//       <Divider />
//       <Stack direction="row" spacing={2}>
//         <Button variant="contained" onClick={onSubmit} disabled={submitting}>
//           {mode === 'new' ? '作成' : '更新'}
//         </Button>
//       </Stack>
//     </Stack>
//   );
// };
